import clsx from 'clsx'
import { debounce } from 'lodash'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { LocationApi, LocationSearchResult } from 'src/api/location'
import { IconSearch } from 'src/icons'
import Style from './style.module.scss'

interface IProps {
  id?: string
  location?: string
  onChange?: (location: string) => void
  error?: string
  disabled?: boolean
  height?: number
}

export const LocationSearch: FC<IProps> = (props) => {
  const [focus, setFocus] = useState(false)
  const [inputValue, setInputValue] = useState(props.location || '')
  const [suggestions, setSuggestions] = useState<LocationSearchResult['ResultItems']>([])
  // Track loading state for potential UI indicators
  const [isLoading, setIsLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(props.location || '')
  }, [props.location])

  // Debounced search function to avoid too many API calls
  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      if (!query || query.length < 3) {
        setSuggestions([])
        return
      }

      setIsLoading(true)
      try {
        const response = await LocationApi.searchUSLocations(query)
        setSuggestions(response.data.ResultItems || [])
        setShowSuggestions(true)
      } catch (error) {
        console.error('Error searching locations:', error)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 300)
  ).current

  const handleSearch = useCallback((query: string) => {
    debouncedSearch(query)
  }, [debouncedSearch])
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setInputValue(value)
      props.onChange?.(value)
      handleSearch(value)
    },
    [handleSearch, props]
  )

  const handleSelectSuggestion = useCallback(
    (suggestion: LocationSearchResult['ResultItems'][0]) => {
      const location = suggestion.Title
      setInputValue(location)
      props.onChange?.(location)
      setSuggestions([])
      setShowSuggestions(false)
      setActiveIndex(-1)
    },
    [props]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || suggestions.length === 0) return

      // Arrow down
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prevIndex) => prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex)
      } else if (e.key === 'ArrowUp') { // Arrow up
        e.preventDefault()
        setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0))
      } else if (e.key === 'Enter') { // Enter
        e.preventDefault()
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[activeIndex])
        }
      } else if (e.key === 'Escape') { // Escape
        setShowSuggestions(false)
        setActiveIndex(-1)
      }
    },
    [showSuggestions, suggestions, activeIndex, handleSelectSuggestion]
  )

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node) && inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div>
      <div
        className={clsx(Style.inputContainer, {
          [Style.focused]: focus,
          [Style.error]: !!props.error,
          [Style.disabled]: !!props.disabled
        })}
        style={{
          height: props.height || 48
        }}
      >
        <input
          ref={inputRef}
          id={props.id}
          type="text"
          className={Style.input}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            setFocus(true)
            if (inputValue && inputValue.length >= 3) {
              setShowSuggestions(true)
            }
          }}
          onBlur={() => setFocus(false)}
          onKeyDown={handleKeyDown}
          disabled={props.disabled}
          placeholder="Enter location"
        />

        <IconSearch className={Style.iconSearch}/>
        {isLoading && (
          <div ref={suggestionsRef} className={Style.suggestions}>
            <div className={Style.suggestionItem}>
              <div className={Style.skeleton}/>
              <div className={Style.skeleton}/>
              <div className={Style.skeleton}/>
              <div className={Style.skeletonShort}/>
            </div>
          </div>
        )}
        {!isLoading && showSuggestions && suggestions.length > 0 && (
          <div ref={suggestionsRef} className={Style.suggestions}>
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.PlaceId}
                className={clsx(Style.suggestionItem, { [Style.active]: index === activeIndex })}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {suggestion.Title}
              </div>
            ))}
          </div>
        )}
      </div>
      {props.error && (
        <span className="fs-12 fx fx-ai-center gap-1 txt-negative-500 mt-2">
          {/* <IconWarning size={16}/> */}
          {props.error}
        </span>
      )}
    </div>
  )
}
